import { getConcurrentCrops, getFieldCroppingArea, ICropping, IField, IOmniaShell, omniaResolve, str } from "../../omnia"
import { IFieldsService } from "../../types"

export interface ChangeConfirmation {
    cancel?: boolean
    changeCroppingArea?: boolean
    createUncroppedAreas?: boolean
}

export function confirmChanges(context: {
    shell: IOmniaShell,
    svc: IFieldsService,
    target: IField,
    fieldId: number,
    initialArea: number,
    hasNewUncroppedAreas: boolean
}): ng.IPromise<ChangeConfirmation> {

    const { shell, svc, target, fieldId, initialArea, hasNewUncroppedAreas } = context

    const cropping = svc.cropping.filter(c => c.currentYear && (c.fieldId == fieldId)).map(c => c.cropping)

    const shouldConfirmUncroppedAreas = hasNewUncroppedAreas && (!checkForConcurrentCrops(cropping))

    if (shouldConfirmUncroppedAreas) {
        return confirmUncroppedAreas(shell, target, cropping, initialArea)
    }

    return confirmFieldAreaChange(shell, target, cropping, initialArea).then(ok => ({
        changeCroppingArea: ok === true,
        cancel: ok === undefined
    }))

}

function confirmUncroppedAreas(shell: IOmniaShell, target: IField, cropping: ICropping[], initialArea: number): ng.IPromise<ChangeConfirmation> {

    const title = "Update crop boundaries"
    const msg = "The field boundary has changed, and contains uncropped areas<br><br>Do you wish change the current cropping area, or to have new ones created?"

    const msgNewAreas = "Create new areas"
    const msgChangeAreas = "Change the area"

    const area = getFieldCroppingArea(target)

    return shell.showMessageBox({
        title,
        content: msg,
        buttons: [
            { text: str.Cancel, value: null, cssClass: "btn-warning" },
            { text: msgNewAreas, value: false, cssClass: "btn-default btn-min-width" },
            { text: msgChangeAreas, value: true, cssClass: "btn-default btn-min-width" }
        ]
    }).then(r => {

        if (r == null) {
            return { cancel: true }
        }

        const result = { changeCroppingArea: !!r, createUncroppedAreas: !r, cancel: false }

        return (area != initialArea)
            ? confirmImpactOnPlans(shell, cropping).then(ok => ({ ...result, cancel: !ok }))
            : result

    })

}

function confirmFieldAreaChange(shell: IOmniaShell, target: IField, cropping: ICropping[], initialArea: number) {

    const area = getFieldCroppingArea(target)

    if (initialArea == area) {
        return omniaResolve(false)
    }

    const title = "Update crop area"
    const msg = `You have changed the field area from ${initialArea} to ${area} ha<br><br>Do you wish to change the crop area to match?`

    return omnia.shell.showYesNoCancel(shell, msg, title)
        .then(ok => {

            if (!ok) {
                return ok;
            }

            return confirmImpactOnPlans(shell, cropping).then(r => r ? true : undefined)

        })

}

function confirmImpactOnPlans(shell: IOmniaShell, cropping: ICropping[]): ng.IPromise<boolean> {

    const plans = cropping.some(c => c.Plans && (c.Plans.length > 0))

    if (!plans) {
        return shell.resolve(true)
    }

    const msg = `<p>This field has crops that are already part of a plan, and changing their area will change the product totals required within the plan.</p>
            <p>You need to recalculate the plans for the new area to be used.</p>`

    const title = "Recalculate plans"

    return omnia.shell.showOkCancel(shell, msg, title).then(ok => !!ok)

}


function checkForConcurrentCrops(cropping: ICropping[]) {

    return cropping.some(c => {
        const others = cropping.filter(o => o != c)
        return getConcurrentCrops(c, { cropping: others }).some(c => true)
    })

}
